package fr.manyverse.app.presentation.components.modules.lockpicking

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Lock
import androidx.compose.material.icons.filled.LockOpen
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import fr.manyverse.app.domain.model.ActiveLockpicking
import fr.manyverse.app.domain.model.LockpickingStatus
import fr.manyverse.app.presentation.theme.success
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.lockpicking_active
import manyverse.shared.generated.resources.lockpicking_cancel_activity
import manyverse.shared.generated.resources.lockpicking_close
import manyverse.shared.generated.resources.lockpicking_locks_progress
import manyverse.shared.generated.resources.lockpicking_target_player
import manyverse.shared.generated.resources.module_status_active
import manyverse.shared.generated.resources.module_status_cancelled
import manyverse.shared.generated.resources.module_status_success
import manyverse.shared.generated.resources.module_status_timeout
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

/**
 * Section displayed in ModulesTab for the GM to monitor an active lockpicking challenge.
 */
@Composable
fun LockpickingGmSection(
    activeLockpicking: ActiveLockpicking?,
    targetPlayerName: String,
    onCancel: () -> Unit,
    onClose: () -> Unit,
    modifier: Modifier = Modifier
) {
    if (activeLockpicking == null) return

    val targetName = targetPlayerName

    Column(
        modifier = modifier
            .fillMaxWidth()
            .padding(top = 16.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Text(
            text = stringResource(Res.string.lockpicking_active),
            style = MaterialTheme.typography.titleSmall,
            color = MaterialTheme.colorScheme.primary
        )

        Card(
            modifier = Modifier.fillMaxWidth(),
            colors = CardDefaults.cardColors(
                containerColor = MaterialTheme.colorScheme.surfaceVariant
            )
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(12.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                // Header row: target player + status indicator + action button
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Icon(
                        imageVector = when (activeLockpicking.status) {
                            LockpickingStatus.SUCCESS -> Icons.Default.LockOpen
                            else -> Icons.Default.Lock
                        },
                        contentDescription = stringResource(activeLockpicking.status.labelRes()),
                        tint = when (activeLockpicking.status) {
                            LockpickingStatus.ACTIVE -> MaterialTheme.colorScheme.primary
                            LockpickingStatus.SUCCESS -> MaterialTheme.colorScheme.success
                            LockpickingStatus.TIMEOUT -> MaterialTheme.colorScheme.error
                            LockpickingStatus.CANCELLED -> MaterialTheme.colorScheme.onSurfaceVariant
                        },
                        modifier = Modifier.size(24.dp)
                    )

                    Column(modifier = Modifier.weight(1f)) {
                        Text(
                            text = "${stringResource(Res.string.lockpicking_target_player)}: $targetName",
                            style = MaterialTheme.typography.titleSmall
                        )
                        Text(
                            text = stringResource(
                                Res.string.lockpicking_locks_progress,
                                activeLockpicking.unlockedCount,
                                activeLockpicking.lockCount
                            ),
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }

                    if (activeLockpicking.status != LockpickingStatus.ACTIVE) {
                        IconButton(onClick = onClose) {
                            Icon(
                                imageVector = Icons.Default.Close,
                                contentDescription = stringResource(Res.string.lockpicking_close),
                                tint = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                }

                // Cancel button (text version) for ACTIVE status
                if (activeLockpicking.status == LockpickingStatus.ACTIVE) {
                    TextButton(
                        onClick = onCancel,
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text(
                            text = stringResource(Res.string.lockpicking_cancel_activity),
                            color = MaterialTheme.colorScheme.error
                        )
                    }
                }
            }
        }
    }
}

private fun LockpickingStatus.labelRes(): StringResource = when (this) {
    LockpickingStatus.ACTIVE -> Res.string.module_status_active
    LockpickingStatus.SUCCESS -> Res.string.module_status_success
    LockpickingStatus.TIMEOUT -> Res.string.module_status_timeout
    LockpickingStatus.CANCELLED -> Res.string.module_status_cancelled
}
